/**
 * Payments Resource Client
 * Axios-based API client for payment endpoints
 */
export class PaymentsResource {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    /**
     * Create payment instrument
     */
    async createPaymentInstrument(paymentInstrumentData) {
        return this.apiClient.post('/api/v1/payment/create-payment-instrument', {
            paymentInstrumentData,
        });
    }
    /**
     * Create card payment instrument
     */
    async createCardPaymentInstrument(basisTheory, cardData) {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }
        console.log('Creating card payment instrument');
        // Create token using BasisTheory
        const btResponse = await basisTheory.tokens.create({
            type: 'card',
            data: {
                cvc: cardData.cvc,
                number: Number(cardData.cardNumber.replace(/\s+/g, '')),
                ...this.getCardMonthAndYear(cardData.expiryDate),
            },
            metadata: {
                nonSensitiveField: 'nonSensitiveValue',
            },
        });
        // Create payment instrument through API
        const { expiration_month, expiration_year } = this.getCardMonthAndYear(cardData.expiryDate);
        const paymentInstrumentData = {
            type: btResponse.type,
            card: {
                maskedCardNumber: btResponse.enrichments?.cardDetails?.bin ?
                    `${btResponse.enrichments.cardDetails.bin}****${btResponse.enrichments.cardDetails.last4}` :
                    cardData.cardNumber.replace(/\d(?=\d{4})/g, '*'),
                expirationMonth: expiration_month,
                expirationYear: expiration_year,
            },
            token: btResponse.id,
        };
        const response = await this.apiClient.post('/api/v1/payment/create-payment-instrument', { paymentInstrumentData });
        console.log('Payment instrument created:', response);
        return response;
    }
    /**
     * Create Apple Pay payment instrument
     */
    async createApplePayPaymentInstrument(basisTheory, applePayToken) {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }
        if (!applePayToken.id) {
            throw new Error('Apple Pay token is missing');
        }
        const paymentInstrumentData = {
            type: 'apple_pay',
            token: applePayToken.id,
            dpanType: applePayToken.type,
            card: {
                bin: applePayToken.card.bin,
                last4: applePayToken.card.last4,
                expirationMonth: applePayToken.card.expiration_month,
                expirationYear: applePayToken.card.expiration_year,
                brand: applePayToken.card.brand,
            },
        };
        const response = await this.apiClient.post('/api/v1/payment/create-payment-instrument', { paymentInstrumentData });
        return response;
    }
    /**
     * Create Google Pay payment instrument
     */
    async createGooglePayPaymentInstrument(basisTheory, googlePayToken) {
        if (!basisTheory) {
            throw new Error('Payment processor not initialized');
        }
        if (!googlePayToken.id) {
            throw new Error('Google Pay token is missing');
        }
        const paymentInstrumentData = {
            type: 'google_pay',
            token: googlePayToken.id,
            dpanType: googlePayToken.type,
            card: {
                bin: googlePayToken.card.bin,
                last4: googlePayToken.card.last4,
                expirationMonth: googlePayToken.card.expiration_month,
                expirationYear: googlePayToken.card.expiration_year,
                brand: googlePayToken.card.brand,
            },
            authMethod: googlePayToken?.details?.auth_method,
        };
        const response = await this.apiClient.post('/api/v1/payment/create-payment-instrument', { paymentInstrumentData });
        return response;
    }
    /**
     * Create APM payment instrument (Klarna, Afterpay, PayPal, etc.)
     */
    async createApmPaymentInstrument(apmData) {
        console.log('Creating APM payment instrument:', apmData);
        const paymentInstrumentData = {
            type: 'apm',
            provider: apmData.provider,
            paymentMethod: apmData.paymentMethod,
            processorId: apmData.processorId,
        };
        const response = await this.apiClient.post('/api/v1/payment/create-payment-instrument', { paymentInstrumentData });
        console.log('APM payment instrument created:', response);
        return response;
    }
    /**
     * Helper function to format expiry date
     */
    getCardMonthAndYear(expiryDate) {
        const [month, year] = expiryDate.split('/');
        const currentYear = new Date().getFullYear();
        const century = Math.floor(currentYear / 100) * 100;
        const fullYear = Number(year) + century;
        return {
            expiration_month: Number(month),
            expiration_year: fullYear,
        };
    }
    /**
     * Process payment directly with checkout session
     */
    async processPaymentDirect(checkoutSessionId, paymentInstrumentId, threedsSessionId, options = {}) {
        console.log('[PaymentsResource] processPaymentDirect START', options.paymentFlowId ? `(flow: ${options.paymentFlowId})` : '');
        console.log('[PaymentsResource] Full options object:', JSON.stringify(options, null, 2));
        console.log('[PaymentsResource] processorId:', options.processorId, 'paymentMethod:', options.paymentMethod);
        try {
            const requestBody = {
                checkoutSessionId,
                paymentInstrumentId,
                ...(threedsSessionId && { threedsSessionId }),
                ...(options.initiatedBy && { initiatedBy: options.initiatedBy }),
                ...(options.source && { source: options.source }),
                ...(options.paymentFlowId && { paymentFlowId: options.paymentFlowId }),
                ...(options.processorId && { processorId: options.processorId }),
                ...(options.paymentMethod && { paymentMethod: options.paymentMethod }),
                ...(options.blikCode && { blikCode: options.blikCode }),
                ...(options.isExpress && { isExpress: options.isExpress }),
                ...(options.shippingRateId && { shippingRateId: options.shippingRateId }),
            };
            console.log('[PaymentsResource] Request body being sent:', JSON.stringify(requestBody, null, 2));
            const response = await this.apiClient.post('/api/public/v1/checkout/pay-v2', requestBody);
            console.log('[PaymentsResource] processPaymentDirect SUCCESS:', response);
            return response;
        }
        catch (error) {
            console.error('[PaymentsResource] processPaymentDirect ERROR:', error);
            throw error;
        }
    }
    /**
     * Get card payment instruments for customer
     */
    async getCardPaymentInstruments() {
        return this.apiClient.get('/api/v1/payment-instruments/customer');
    }
    /**
     * Mark payment action as processed
     */
    async markPaymentActionProcessed(paymentId) {
        return this.apiClient.post('/api/v1/payments/require-action/processed', {
            paymentId,
        });
    }
    /**
     * Get payment status
     */
    async getPaymentStatus(paymentId) {
        return this.apiClient.get(`/api/v1/payments/${paymentId}`);
    }
    /**
     * Retrieve payment status from processor
     * Used for external payment flows that require server-side status checks
     */
    async retrievePayment(paymentId) {
        return this.apiClient.post('/api/v1/payments/retrieve', {
            paymentId,
        });
    }
    async saveRadarSession(data) {
        return this.apiClient.post('/api/v1/radar-sessions', data);
    }
    /**
     * Complete payment after an action (3DS, radar, etc.)
     * This resumes the payment flow that was paused for additional authentication/verification
     */
    async completePaymentAfterAction(paymentId) {
        return this.apiClient.post('/api/v1/payments/complete-after-3ds', {
            paymentId,
        });
    }
    /**
     * Update 3DS session status (used for Airwallex 3DS return handling)
     */
    async updateThreedsStatus(data) {
        return this.apiClient.post('/api/v1/threeds/status', data);
    }
    /**
     * Complete N-Genius payment after WebSDK 3DS flow finishes.
     * Verifies state from N-Genius server-side and updates the payment record.
     */
    async ngeniusThreedsComplete(data) {
        return this.apiClient.post('/api/v1/payments/ngenius/threeds-complete', data);
    }
}
